import React, { useState } from "react";
import { X, Scale, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePins, togglePin, clearPins } from "@/lib/compareStore";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Floating bottom-right tray that appears once ≥1 item is pinned.
 * Click "Compare" → opens a side-by-side modal with the pinned items.
 */
export default function CompareTray() {
  const pins = usePins();
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);

  if (!pins || pins.length === 0) return null;

  return (
    <>
      <div
        className="fixed bottom-4 right-4 z-40 hidden max-w-md flex-col rounded-2xl border border-slate-200 bg-white p-3 shadow-lg sm:flex"
        data-testid="compare-tray"
      >
        <div className="mb-2 flex items-center justify-between px-1">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
            <Scale className="h-3.5 w-3.5 text-teal-700" />
            {lang === "fr" ? `${pins.length} épinglé${pins.length > 1 ? "s" : ""}` : `${pins.length} pinned`}
          </div>
          <button
            onClick={() => clearPins()}
            className="text-xs font-semibold text-slate-500 hover:text-slate-900"
            data-testid="compare-clear"
          >
            {lang === "fr" ? "Effacer" : "Clear"}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {pins.map((p) => (
            <span
              key={`${p.kind}-${p.slug}`}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700"
              data-testid={`compare-chip-${p.kind}-${p.slug}`}
            >
              <span className="truncate max-w-[140px]">{p.name}</span>
              <button
                onClick={() => togglePin(p)}
                className="text-slate-400 hover:text-slate-700"
                aria-label="remove"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <Button
          size="sm"
          className="mt-3 w-full bg-teal-700 font-bold uppercase tracking-wider hover:bg-teal-800"
          onClick={() => setOpen(true)}
          data-testid="compare-open-btn"
        >
          {lang === "fr" ? "Comparer" : "Compare"} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Mobile mini tray */}
      <div
        className="fixed bottom-3 left-3 right-3 z-40 flex items-center justify-between rounded-full border border-slate-200 bg-white px-4 py-2 shadow-lg sm:hidden"
        data-testid="compare-tray-mobile"
      >
        <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
          <Scale className="h-3.5 w-3.5 text-teal-700" />
          {pins.length}{" "}
          {lang === "fr" ? `épinglé${pins.length > 1 ? "s" : ""}` : "pinned"}
        </div>
        <Button
          size="sm"
          className="rounded-full bg-teal-700 text-xs hover:bg-teal-800"
          onClick={() => setOpen(true)}
        >
          {lang === "fr" ? "Comparer" : "Compare"}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl" data-testid="compare-modal">
          <DialogHeader>
            <DialogTitle>
              {lang === "fr" ? "Comparaison côte à côte" : "Side-by-side comparison"}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-auto">
            <CompareTable pins={pins} lang={lang} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CompareTable({ pins, lang }) {
  // Union of every attribute across the pinned items so the table renders
  // one row per attribute across all columns.
  const keys = Array.from(
    new Set(pins.flatMap((p) => Object.keys(p.meta || {})))
  );

  return (
    <table className="min-w-full text-sm" data-testid="compare-table">
      <thead className="sticky top-0 bg-white">
        <tr>
          <th className="w-40 border-b border-slate-200 px-3 py-2 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
            {lang === "fr" ? "Attribut" : "Attribute"}
          </th>
          {pins.map((p) => (
            <th
              key={`${p.kind}-${p.slug}`}
              className="border-b border-slate-200 px-3 py-2 text-left text-xs font-bold text-slate-900"
            >
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                {p.kind}
              </div>
              <div className="mt-0.5 text-sm font-black">{p.name}</div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {keys.length === 0 ? (
          <tr>
            <td colSpan={pins.length + 1} className="px-3 py-6 text-center text-xs text-slate-500">
              {lang === "fr"
                ? "Aucune caractéristique commune à afficher."
                : "No shared attributes to display."}
            </td>
          </tr>
        ) : (
          keys.map((k) => (
            <tr key={k} className="border-b border-slate-100">
              <td className="px-3 py-2 text-xs font-semibold text-slate-500">{prettyKey(k, lang)}</td>
              {pins.map((p) => (
                <td key={`${p.kind}-${p.slug}-${k}`} className="px-3 py-2 text-sm text-slate-900">
                  {formatValue(p.meta?.[k])}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function formatValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

const KEY_LABELS_FR = {
  tier: "Catégorie",
  minScore: "Score minimum",
  rate: "Taux",
  term: "Durée",
  note: "Note",
  situation: "Situation",
  rate_range: "Fourchette de taux",
  best_for: "Idéal pour",
  category: "Catégorie",
  reading_time: "Temps de lecture",
  short: "Description",
};
const KEY_LABELS_EN = {
  tier: "Tier",
  minScore: "Minimum score",
  rate: "Rate",
  term: "Term",
  note: "Note",
  situation: "Situation",
  rate_range: "Rate range",
  best_for: "Best for",
  category: "Category",
  reading_time: "Reading time",
  short: "Description",
};

function prettyKey(k, lang) {
  const dict = lang === "fr" ? KEY_LABELS_FR : KEY_LABELS_EN;
  return dict[k] || k;
}
